import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, GripVertical, ArrowUp, ArrowDown, Plus, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PathEntry {
  id: string;
  title: string;
  path_type: string;
  version: number;
  suggestion_importance_rank: number | null;
  progress: {
    path_id: string;
    status: string;
    order_index: number | null;
    started_at: string | null;
    completed_at: string | null;
  } | null;
}

interface UserPathsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: { user_id: string; email: string | null } | null;
}

const statusLabels: Record<string, string> = {
  to_do: "À faire",
  hold: "En attente",
  in_progress: "En cours",
  standby: "Standby",
  completed: "Terminé",
};

const statusColors: Record<string, string> = {
  to_do: "bg-primary/15 text-primary border-primary/30",
  hold: "bg-muted text-muted-foreground border-border",
  in_progress: "bg-amber-500/15 text-amber-700 border-amber-500/30",
  standby: "bg-orange-500/15 text-orange-700 border-orange-500/30",
  completed: "bg-emerald-500/15 text-emerald-700 border-emerald-500/30",
};

export function UserPathsDialog({ open, onOpenChange, user }: UserPathsDialogProps) {
  const [paths, setPaths] = useState<PathEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dragItemRef = useRef<number | null>(null);

  const fetchPaths = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const params = new URLSearchParams({ action: "list_paths", user_id: user.user_id });
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users?${params}`,
        { headers: { Authorization: `Bearer ${session?.access_token}`, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setPaths(data.paths || []);
      setOrderedIds((data.paths || []).map((p: PathEntry) => p.id));
      setHasChanges(false);
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (open && user) fetchPaths();
  }, [open, user, fetchPaths]);

  const moveItem = (index: number, direction: -1 | 1) => {
    const newOrder = [...orderedIds];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newOrder.length) return;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setOrderedIds(newOrder);
    setHasChanges(true);
  };

  const handleDragStart = (index: number) => {
    dragItemRef.current = index;
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragItemRef.current !== null && dragOverIndex !== null && dragItemRef.current !== dragOverIndex) {
      const newOrder = [...orderedIds];
      const [removed] = newOrder.splice(dragItemRef.current, 1);
      newOrder.splice(dragOverIndex, 0, removed);
      setOrderedIds(newOrder);
      setHasChanges(true);
    }
    dragItemRef.current = null;
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const addPath = (pathId: string) => {
    setOrderedIds(prev => [...prev, pathId]);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-users?action=reorder_paths`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ user_id: user.user_id, ordered_path_ids: orderedIds }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      toast.success("Parcours mis à jour");
      await fetchPaths();
    } catch (e: any) {
      toast.error(e.message || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const pathMap = new Map(paths.map(p => [p.id, p]));
  const orderedPaths = orderedIds.map(id => pathMap.get(id)).filter(Boolean) as PathEntry[];
  const uninitializedPaths = paths.filter(p => !orderedIds.includes(p.id));

  const userName = user?.email || user?.user_id.slice(0, 8) || "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Parcours de {userName}</DialogTitle>
          <DialogDescription>
            Visualisez et réordonnez les parcours initialisés. Ajoutez les parcours publiés non encore initialisés.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin h-6 w-6 text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Ordered paths */}
            <div className="space-y-1">
              <h3 className="text-sm font-medium text-muted-foreground mb-2">
                Parcours ordonnés ({orderedPaths.length})
              </h3>
              {orderedPaths.length === 0 ? (
                <p className="text-sm text-muted-foreground italic py-2">Aucun parcours initialisé</p>
              ) : (
                orderedPaths.map((p, i) => (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={(e) => handleDragOver(e, i)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 bg-card transition-all ${
                      dragIndex === i ? "opacity-50" : ""
                    } ${dragOverIndex === i && dragIndex !== i ? "border-primary border-2" : ""}`}
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground shrink-0 cursor-grab active:cursor-grabbing" />
                    <span className="text-sm font-medium text-muted-foreground w-6">{i + 1}</span>
                    <span className="flex-1 text-sm font-medium truncate">{p.title}</span>
                    <Badge variant="default" className="text-xs py-0">
                      v{p.version}
                    </Badge>
                    {p.progress && (
                      <Badge variant="outline" className={`text-xs ${statusColors[p.progress.status] || ""}`}>
                        {statusLabels[p.progress.status] || p.progress.status}
                      </Badge>
                    )}
                    {!p.progress && (
                      <Badge variant="outline" className="text-xs bg-amber-500/15 text-amber-700 border-amber-500/30">
                        Nouveau
                      </Badge>
                    )}
                    <div className="flex gap-0.5">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={i === 0}
                        onClick={() => moveItem(i, -1)}
                        title="Monter"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        disabled={i === orderedPaths.length - 1}
                        onClick={() => moveItem(i, 1)}
                        title="Descendre"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Uninitialized paths */}
            {uninitializedPaths.length > 0 && (
              <div className="space-y-1">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">
                  Parcours non initialisés ({uninitializedPaths.length})
                </h3>
                {uninitializedPaths.map(p => (
                  <div
                    key={p.id}
                    className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2"
                  >
                    <span className="flex-1 text-sm text-muted-foreground truncate">{p.title}</span>
                    <Badge variant="default" className="text-xs py-0">
                      v{p.version}
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={() => addPath(p.id)}
                    >
                      <Plus className="h-3 w-3" />
                      Ajouter
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Save button */}
            {hasChanges && (
              <div className="flex justify-end pt-2 border-t">
                <Button onClick={handleSave} disabled={saving} className="gap-2">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Enregistrer l'ordre
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
